import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { api } from '@/api/client';
import type { Wallet } from '@/api/types';
import { Card, ErrorNote, Spinner, Timestamp } from '@/components/Common';
import { LevelPill } from '@/components/StatusPill';
import { useAuth } from '@/lib/auth';
import { catTime, money } from '@/lib/format';

export function FloatPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['float'],
    queryFn: () => api.get<{ wallets: Wallet[] }>('/api/admin/float'),
    refetchInterval: 30_000,
  });

  const refresh = useMutation({
    mutationFn: () => api.get<{ wallets: Wallet[] }>('/api/admin/float?fresh=true'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['float'] }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  const wallets = data?.wallets ?? [];
  const active = selected ?? wallets[0]?.accountTypeId ?? null;

  return (
    <div className="space-y-4">
      <Card
        title="Wallets"
        actions={
          can('ADMIN', 'OPS') && (
            <button type="button" className="btn-secondary" disabled={refresh.isPending}
                    onClick={() => refresh.mutate()}>
              {refresh.isPending ? 'Polling…' : 'Poll upstream now'}
            </button>
          )
        }
      >
        <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
          {wallets.map((wallet) => (
            <button
              key={wallet.accountTypeId}
              type="button"
              onClick={() => setSelected(wallet.accountTypeId)}
              className={`rounded-lg border p-4 text-left transition-colors ${
                active === wallet.accountTypeId ? 'border-ink-800 bg-ink-50' : 'border-ink-200'
              } ${wallet.level === 'CRITICAL' ? 'border-red-400' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span className="label">{wallet.name}</span>
                <LevelPill level={wallet.level} />
              </div>
              <div className="tabular mt-2 text-2xl font-semibold">{money(wallet.balance)}</div>
              {wallet.hoursRemaining && (
                <div className="mt-1 text-xs text-amber-800">
                  ~{wallet.hoursRemaining}h left at {money(wallet.hourlyBurnRate)}/h
                </div>
              )}
              <div className="mt-1 text-xs text-ink-500">
                warn {money(wallet.warnBelow)} · critical {money(wallet.criticalBelow)}
              </div>
              <div className="mt-1 text-xs text-ink-400">
                <Timestamp value={wallet.capturedAt} />
              </div>
            </button>
          ))}
          {wallets.length === 0 && (
            <p className="text-sm text-ink-500">No balance poll has landed yet.</p>
          )}
        </div>
        <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
          Wallets are not fungible: a ZWG balance cannot fund a ZESA token purchase. Transfers
          between them are a manual back-office action at HOT.
        </p>
      </Card>

      {active !== null && <HistoryChart accountTypeId={active} />}
      {can('ADMIN', 'FINANCE') && active !== null && (
        <ThresholdEditor wallet={wallets.find((w) => w.accountTypeId === active)!} />
      )}
    </div>
  );
}

function HistoryChart({ accountTypeId }: { accountTypeId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['float-history', accountTypeId],
    queryFn: () => api.get<{ series: Array<{ capturedAt: string; balance: string }> }>(
      `/api/admin/float/history?accountTypeId=${accountTypeId}&days=7`),
  });

  const points = (data?.series ?? []).map((point) => ({
    at: catTime(point.capturedAt),
    balance: Number(point.balance),
  }));

  return (
    <Card title="Last seven days">
      {isLoading && <Spinner />}
      {!isLoading && points.length < 2 && (
        <p className="px-4 py-10 text-center text-sm text-ink-500">
          Not enough history yet to draw a line.
        </p>
      )}
      {points.length >= 2 && (
        <div className="h-72 p-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid stroke="#eceef2" vertical={false} />
              <XAxis dataKey="at" tick={{ fontSize: 11, fill: '#647691' }}
                     minTickGap={48} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#647691' }} width={80}
                     tickLine={false} axisLine={false}
                     tickFormatter={(value: number) => value.toLocaleString()} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #d5dae2' }}
                formatter={(value: number) => [money(String(value)), 'Balance']}
              />
              <Line type="monotone" dataKey="balance" stroke="#384253"
                    strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function ThresholdEditor({ wallet }: { wallet: Wallet }) {
  const queryClient = useQueryClient();
  const [warn, setWarn] = useState(wallet.warnBelow ?? '');
  const [critical, setCritical] = useState(wallet.criticalBelow ?? '');

  const save = useMutation({
    mutationFn: () => api.put('/api/admin/float/thresholds', {
      accountTypeId: wallet.accountTypeId, warnBelow: warn, criticalBelow: critical,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['float'] }),
  });

  return (
    <Card title={`Thresholds — ${wallet.name}`}>
      <div className="flex flex-wrap items-end gap-3 p-4">
        <label>
          <span className="label">Warn below</span>
          <input className="input mt-1 tabular w-40" value={warn} inputMode="decimal"
                 onChange={(e) => setWarn(e.target.value)} />
        </label>
        <label>
          <span className="label">Critical below</span>
          <input className="input mt-1 tabular w-40" value={critical} inputMode="decimal"
                 onChange={(e) => setCritical(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" disabled={save.isPending}
                onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {save.isSuccess && <span className="text-sm text-emerald-700">Saved</span>}
      </div>
      {save.isError && <ErrorNote error={save.error} />}
      <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
        Below the critical threshold the Platform stops accepting orders against this wallet, so an
        aggressive value costs sales and a slack one costs failed recharges.
      </p>
    </Card>
  );
}
